import { addMinutes } from 'date-fns';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { EventAttributes, SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { getTrelloCardDataFromCardId } from '@trello/business-logic-react/card';
import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { client } from '@trello/graphql';
import { showFlag } from '@trello/nachos/experimental-flags';
import { getSmartCardClient } from '@trello/smart-card/smart-card-client';

import {
  createCardBasedOptimisticResponse,
  createFocusTimeOptimisticResponse,
} from './utils/createOptimisticResponses';
import { getIsInboxBoard } from './utils/getIsInboxBoard';
import {
  PlannerCardDataFragmentDoc,
  type PlannerCardDataFragment,
} from './PlannerCardDataFragment.generated';
import { PLANNER_CONSTANTS } from './plannerConstants';
import { useTrelloCreateCalendarEventMutation } from './TrelloCreateCalendarEventMutation.generated';
import { useLazyLoadCreateEventFields } from './useLazyLoadCreateEventFields';
import { usePlannerAccount } from './usePlannerAccount';
import { usePlannerAccounts } from './usePlannerAccounts';
import { usePlannerRef } from './usePlannerRef';
import { useToggleCalendar } from './useToggleCalendar';
import { useWorkspaceAri } from './useWorkspaceAri';

export type UseCreatePlannerEventArgs = {
  source: SourceType;
  optimisticEventPrefix: string;
};

type CreatePlannerEventArgs = {
  startDate?: Date | null;
  endDate?: Date | null;
  eventCardId?: string;
};

const taskName = 'create-plannerEvent';

/**
 * The "source of truth" for creating Planner events.
 *
 * @param {Object} args - The arguments for the hook.
 * @param {SourceType} args.source - The analytics source for the event.
 * @param {string} args.optimisticEventPrefix - Prefix for optimistic event IDs.
 * @returns {Function} A callback function to create a planner event.
 */
export const useCreatePlannerEvent = ({
  source,
  optimisticEventPrefix,
}: UseCreatePlannerEventArgs): (({
  startDate,
  endDate,
  eventCardId,
}: CreatePlannerEventArgs) => Promise<void>) => {
  const intl = useIntl();
  const memberId = useMemberId();
  const { plannerRef } = usePlannerRef();
  const { workspaceId } = useWorkspaceAri();

  const { primaryAccountId } = usePlannerAccounts();
  const { plannerAccountDisplayName, primaryCalendarId } = usePlannerAccount({
    accountId: primaryAccountId ?? '',
  });

  const { toggleCalendarMutation, toggledCalendarId } = useToggleCalendar();
  const [createCalendarEvent] = useTrelloCreateCalendarEventMutation();
  const [lazyLoadCreateEventFields] = useLazyLoadCreateEventFields();

  const { value: isCreateEventMigrationFFEnabled } = useFeatureGate(
    'electric_planner_create_event_migration',
  );

  const calendarApi = plannerRef.current?.getApi();

  const isDndEvent =
    PLANNER_CONSTANTS.DND_TEMP_EVENT_ID_PREFIX === optimisticEventPrefix;
  const hasCalendarViewDates =
    !!calendarApi?.view.currentStart && !!calendarApi?.view.currentEnd;
  const isPlannerCacheCold = !primaryAccountId;

  return useCallback(
    async ({
      startDate,
      endDate,
      eventCardId,
    }: CreatePlannerEventArgs): Promise<void> => {
      let finalPrimaryAccountId: string | undefined;
      let finalPrimaryCalendarId: string | undefined;
      let finalPrimaryAccountDisplayName: string | undefined;

      if (isCreateEventMigrationFFEnabled) {
        // Only load the create event fields when the cache is cold, i.e.,
        // when Planner hasn't been opened yet. This ensures that the regular
        // already opened Planner creation path is not slowed down by a redundant query
        const lazyLoadedCreateEventFields = isPlannerCacheCold
          ? await lazyLoadCreateEventFields()
          : undefined;

        finalPrimaryAccountId =
          lazyLoadedCreateEventFields?.primaryAccountId ?? primaryAccountId;

        finalPrimaryCalendarId =
          lazyLoadedCreateEventFields?.primaryCalendarId ?? primaryCalendarId;

        finalPrimaryAccountDisplayName =
          lazyLoadedCreateEventFields?.primaryAccountDisplayName ??
          plannerAccountDisplayName;
      } else {
        finalPrimaryAccountId = primaryAccountId;
        finalPrimaryCalendarId = primaryCalendarId;
        finalPrimaryAccountDisplayName = plannerAccountDisplayName;
      }

      const shouldTogglePrimaryCalendar = Boolean(!finalPrimaryCalendarId);
      let toggledPrimaryCalendarId = finalPrimaryCalendarId;

      if (!finalPrimaryAccountId || !startDate) {
        return;
      }

      const traceId = Analytics.startTask({
        taskName,
        source,
      });

      let cardData: PlannerCardDataFragment | null = null;
      let boardId = '';
      let isInboxBoard = false;
      let uiEventAttributes: EventAttributes;

      if (eventCardId) {
        cardData = client.readFragment<PlannerCardDataFragment>({
          id: client.cache.identify({
            __typename: 'Card',
            id: eventCardId,
          }),
          fragment: PlannerCardDataFragmentDoc,
        });
        boardId = cardData?.idBoard || '';
        isInboxBoard = getIsInboxBoard(memberId, boardId);

        uiEventAttributes = {
          personalProductivity: 'planner',
          containerType: 'planner',
          cardId: eventCardId,
          source: isInboxBoard ? 'inbox' : 'board',
          action: 'schedule',
          taskId: traceId,
        };
      } else {
        uiEventAttributes = {
          personalProductivity: 'planner',
          containerType: 'planner',
          taskId: traceId,
        };
      }

      if (isDndEvent) {
        Analytics.sendUIEvent({
          action: 'dropped',
          actionSubject: 'card',
          source,
          attributes: uiEventAttributes,
          containers: formatContainers({ workspaceId }),
        });
      } else {
        const buttonName = eventCardId
          ? 'addPlannerEventFromCardSaveButton'
          : 'saveButton';

        Analytics.sendClickedButtonEvent({
          buttonName,
          source,
          attributes: uiEventAttributes,
          containers: formatContainers({ workspaceId }),
        });
      }

      try {
        if (shouldTogglePrimaryCalendar && finalPrimaryAccountDisplayName) {
          await toggleCalendarMutation({
            providerCalendarId: finalPrimaryAccountDisplayName,
            start: calendarApi?.view.currentStart.toISOString() || '',
            end: calendarApi?.view.currentEnd.toISOString() || '',
            newCheckedState: true,
            traceId,
          });

          toggledPrimaryCalendarId = toggledCalendarId.current;
        }

        let focusEventName: string;
        let cardAri: string | undefined;
        let cardObjectId: string | undefined = undefined;

        if (eventCardId) {
          const {
            cardAri: extractedCardAri,
            cardName,
            cardRole,
            cardObjectId: extractedCardObjectId,
          } = getTrelloCardDataFromCardId(eventCardId);
          focusEventName = cardName || '';
          cardAri = extractedCardAri;
          cardObjectId = extractedCardObjectId || undefined;

          const smartCardClient = getSmartCardClient();
          if (cardRole === 'link') {
            const fetchLinkCardName = async () => {
              const resolved = await smartCardClient
                .fetchData(cardName || '')
                .catch(() => {});
              if (resolved?.data && 'name' in resolved.data) {
                focusEventName = resolved.data.name || '';
              }
            };
            await fetchLinkCardName();
          }

          if (!focusEventName) {
            focusEventName = intl.formatMessage({
              id: 'templates.planner.focus-time',
              defaultMessage: 'Focus time',
              description:
                'Default title for new planner events when a fallback is needed',
            });
          }
        } else {
          focusEventName = intl.formatMessage({
            id: 'templates.planner.focus-time',
            defaultMessage: 'Focus time',
            description:
              'Default title for new planner events when a fallback is needed',
          });
        }

        if (!toggledPrimaryCalendarId) {
          return;
        }

        const eventStart = startDate.toISOString();
        const eventEnd = endDate
          ? endDate.toISOString()
          : addMinutes(
              startDate,
              PLANNER_CONSTANTS.DND_NEW_EVENT_DURATION_IN_MINUTES,
            ).toISOString();

        const optimisticResponse = hasCalendarViewDates
          ? eventCardId
            ? createCardBasedOptimisticResponse({
                toggledPrimaryCalendarId,
                optimisticEventPrefix,
                eventCardId,
                focusEventName,
                eventStart,
                eventEnd,
                cardAri: cardAri!,
                cardObjectId: cardObjectId!,
              })
            : createFocusTimeOptimisticResponse({
                toggledPrimaryCalendarId,
                optimisticEventPrefix,
                focusEventName,
                eventStart,
                eventEnd,
              })
          : undefined;

        await createCalendarEvent({
          variables: {
            start: calendarApi?.view.currentStart.toISOString() || '',
            end: calendarApi?.view.currentEnd.toISOString() || '',
            plannerCalendarId: toggledPrimaryCalendarId,
            providerAccountId: finalPrimaryAccountId,
            event: {
              title: focusEventName,
              ...(cardAri && { cardId: cardAri }),
              start: eventStart,
              end: eventEnd,
            },
          },
          context: {
            traceId,
          },
          optimisticResponse,
          // When creating an event with Planner closed and the cache is cold,
          // we don't want to write a null `start` and `end` window to the `events:{}`
          // key in the cache.
          fetchPolicy: isPlannerCacheCold ? 'no-cache' : undefined,
        });

        Analytics.sendTrackEvent({
          action: 'created',
          actionSubject: 'plannerEvent',
          source,
          attributes: {
            personalProductivity: 'planner',
            containerType: 'planner',
            method: isDndEvent
              ? 'dnd'
              : eventCardId
                ? 'cardQuickActions'
                : 'popover',
            taskId: traceId,
          },
          containers: formatContainers({
            workspaceId,
          }),
        });

        Analytics.taskSucceeded({ taskName, source, traceId });
      } catch (error) {
        Analytics.taskFailed({ taskName, source, traceId, error });

        const tempEvent = calendarApi?.getEventById(
          `${optimisticEventPrefix}${eventCardId || 'focus-time'}`,
        );

        if (tempEvent) {
          try {
            tempEvent.remove();
          } catch (removeError) {
            sendErrorEvent(removeError, {
              tags: {
                ownershipArea: 'trello-electric',
                feature: 'Planner',
              },
              extraData: {
                component: isDndEvent
                  ? 'useHandleDrop'
                  : eventCardId
                    ? 'AddCardToPlannerPopover'
                    : 'EventDetailPopoverFooter',
              },
            });

            throw removeError;
          }
        }

        const errorMessage = eventCardId
          ? intl.formatMessage({
              id: 'templates.planner.add-card-to-event-error',
              defaultMessage:
                'We weren’t able to add an event for this card, please try again',
              description: 'Error flag message for creating a calendar event',
            })
          : intl.formatMessage({
              id: 'templates.planner.create-calendar-focus-event-error',
              defaultMessage:
                'We weren’t able to add focus time, please try again',
              description: 'Error flag message for creating a calendar event',
            });

        showFlag({
          id: 'plannerCreateEventError',
          title: errorMessage,
          appearance: 'error',
          isAutoDismiss: true,
        });
      }
    },
    [
      lazyLoadCreateEventFields,
      primaryAccountId,
      primaryCalendarId,
      source,
      memberId,
      isDndEvent,
      workspaceId,
      plannerAccountDisplayName,
      createCalendarEvent,
      calendarApi,
      intl,
      hasCalendarViewDates,
      optimisticEventPrefix,
      toggleCalendarMutation,
      toggledCalendarId,
      isPlannerCacheCold,
      isCreateEventMigrationFFEnabled,
    ],
  );
};
